(function () {
  if (window.CEGO_CART_QUANTITY) return;

  let pending = Promise.resolve();
  let checkoutPending = false;
  let currentCart = null;
  const verifiedForms = new WeakSet();
  const verifiedLinks = new WeakSet();

  function enqueue(operation) {
    const result = pending.then(operation);
    pending = result.catch(() => {});
    return result;
  }

  function cartUrl(action) {
    return (window.Shopify?.routes?.root || '/') + 'cart' + action + '.js';
  }

  function sectionsUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    if (window.Shopify?.theme?.role?.toLowerCase() !== 'main') {
      const previewId = document.querySelector('cego-shipping-country[data-preview-theme]')?.dataset.previewTheme ||
        window.Shopify?.theme?.id || url.searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
    }
    return url.pathname + url.search;
  }

  function reportError(phase, error) {
    console.warn('[CEGO cart]', { phase, name: error?.name, message: error?.message });
  }

  async function request(url, data) {
    if (data?.sections?.length && window.Shopify?.theme?.role !== 'main') {
      const previewId = document.querySelector('cego-shipping-country[data-preview-theme]')?.dataset.previewTheme ||
        window.Shopify?.theme?.id || new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) {
        const sectionsUrl = new URL(data.sections_url || window.location.pathname, window.location.origin);
        sectionsUrl.searchParams.set('preview_theme_id', previewId);
        data = { ...data, sections_url: sectionsUrl.pathname + sectionsUrl.search };
      }
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        method: data ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data ? JSON.stringify(data) : undefined,
        cache: 'no-store',
        signal: controller.signal
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(state.description || 'Cart update failed');
      return state;
    } finally {
      clearTimeout(timeout);
    }
  }

  function summary(properties) {
    for (const key of ['Quantit\u00e0', 'Quantity']) {
      const match = String(properties?.[key] || '').match(/^(\d+)\s+(?:colli|collo|boxes|box)\s*-\s*(\d+(?:[.,]\d+)?)\s+(MQ|ML|PZ|sqm|lm|pc)\s+(?:totali|total)$/i);
      if (!match) continue;
      const boxes = Number(match[1]);
      const total = Number(match[2].replace(',', '.'));
      const unit = match[3].toUpperCase();
      if (boxes > 0 && total > 0) {
        return { key, content: total / boxes, unit: unit === 'SQM' ? 'MQ' : unit === 'LM' ? 'ML' : unit === 'PC' ? 'PZ' : unit };
      }
    }
    return null;
  }

  function buildProperties(item, quantity) {
    const parsed = summary(item.properties);
    if (!parsed || !Number.isInteger(quantity) || quantity <= 0) return null;
    const english = (document.documentElement.lang || window.Shopify?.locale || '').toLowerCase().startsWith('en');
    const properties = { ...item.properties };
    const key = english ? 'Quantity' : 'Quantit\u00e0';
    const unit = english ? { MQ: 'sqm', ML: 'lm', PZ: 'pc' }[parsed.unit] : parsed.unit;
    const total = (Math.round(parsed.content * quantity * 100) / 100).toFixed(2);
    delete properties[english ? 'Quantit\u00e0' : 'Quantity'];
    properties[key] = english
      ? quantity + ' boxes - ' + total + ' ' + unit + ' total'
      : quantity + ' colli - ' + total.replace('.', ',') + ' ' + unit + ' totali';
    return properties;
  }

  function signature(properties) {
    return JSON.stringify(Object.keys(properties || {}).sort().map(key => [key, properties[key]]));
  }

  function extraProperties(properties) {
    const result = { ...properties };
    delete result['Quantit\u00e0'];
    delete result.Quantity;
    return signature(result);
  }

  function refreshCartItems(cart) {
    currentCart = cart;
    const previous = window.CEGO_CART_ITEMS || [];
    if (window.CEGO_CART_ITEMS) window.CEGO_CART_ITEMS = cart.items.map((item, index) => {
      const old = previous.find(candidate => candidate.key === item.key) ||
        previous.find(candidate => String(candidate.variant_id || candidate.key?.split(':')[0]) === String(item.variant_id) &&
          extraProperties(candidate.properties) === extraProperties(item.properties));
      return { ...old, ...item, line: index + 1, title: item.product_title, productType: item.product_type,
        priceCents: item.original_price ?? item.price,
        finalPriceCents: item.final_price ?? item.original_price ?? item.price,
        originalLinePrice: item.original_line_price,
        finalLinePrice: item.final_line_price,
        priceSource: 'presentment', taxable: item.taxable !== false,
        quantity: item.quantity, properties: item.properties || {} };
    });
    renderPrices();
  }

  function renderPrices() {
    if (!currentCart || !window.CegoPricing) return;
    const pricing = window.CegoPricing;
    const update = (element, cents, source, taxable) => {
      if (!Number.isFinite(Number(cents))) return;
      element.dataset.cegoPriceSource = source;
      element.dataset.cegoPriceCents = String(cents);
      element.dataset.cegoPriceTaxable = String(taxable);
      const amount = source === 'contextual' ? Number(cents) : pricing.presentment(cents, taxable);
      const text = pricing.format(amount);
      if (element.textContent !== text) element.textContent = text;
    };
    let total = 0;
    currentCart.items.forEach((item, index) => {
      const taxable = item.taxable !== false;
      const paidLine = item.line_price ?? (item.total_discount != null
        ? item.original_line_price - item.total_discount
        : item.final_line_price);
      total += pricing.presentment(paidLine, taxable);
      for (const prefix of ['CartItem-', 'CartDrawer-Item-']) {
        const row = document.getElementById(prefix + (index + 1));
        if (!row) continue;
        row.querySelectorAll('[data-cego-cart-price-field]').forEach(element => {
          const field = element.dataset.cegoCartPriceField;
          const metadata = window.CEGO_CART_ITEMS?.[index];
          const cents = field === 'original_display_price'
            ? item.original_price !== item.final_price ? item.original_price : metadata?.compareCents || item.original_price
            : item[field];
          element.dataset.ymqItemKey = item.key;
          update(element, cents, 'presentment', taxable);
        });
      }
    });
    document.querySelectorAll('[data-cego-cart-total]').forEach(element => update(element, total, 'contextual', false));
  }

  function findItem(cart, original) {
    return cart.items.find(item => item.key === original.key) ||
      cart.items.find(item => String(item.variant_id) === String(original.variant_id || original.id || original.key?.split(':')[0]) &&
        extraProperties(item.properties) === extraProperties(original.properties));
  }

  async function normalize(cart, sections) {
    const limit = cart.items.length * 2 + 2;
    for (let attempt = 0; attempt < limit; attempt++) {
      const item = cart.items.find(candidate => {
        const properties = buildProperties(candidate, candidate.quantity);
        return properties && signature(properties) !== signature(candidate.properties);
      });
      if (!item) {
        refreshCartItems(cart);
        return cart;
      }
      // Changing properties changes the line key and can merge lines: always use the returned cart.
      cart = await request(cartUrl('/change'), {
        id: item.key,
        quantity: item.quantity,
        properties: buildProperties(item, item.quantity),
        ...(sections?.length ? { sections, sections_url: window.location.pathname } : {})
      });
    }
    throw new Error('Cart quantity properties did not converge');
  }

  function hasSections(state, sections) {
    return sections.every(id => {
      const html = state.sections?.[id];
      if (typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (id === 'cart-notification-product') return !!parsed.getElementById('cart-notification-product-' + state.key);
      return !!parsed.querySelector(id === 'cart-drawer' ? '#CartDrawer' : '.shopify-section');
    });
  }

  async function afterAdd(response, sections = []) {
    let state = response;
    try {
      const cart = await normalize(await request(cartUrl('')), sections);
      const item = findItem(cart, response);
      if (!item) throw new Error('Added cart line could not be verified');
      state = { ...response, ...item, cartItemCount: cart.item_count, sections: cart.sections || response.sections };
    } catch (error) {
      reportError('verify-added-line', error);
      state = { ...state, quantityVerificationPending: true };
    }
    if (!state.quantityVerificationPending && sections.length && !hasSections(state, sections)) {
      try {
        const url = new URL(sectionsUrl(), window.location.origin);
        url.searchParams.set('sections', sections.join(','));
        state = { ...state, sections: await request(url.pathname + url.search) };
      } catch (error) {
        reportError('refresh-cart-sections', error);
      }
    }
    return state;
  }

  async function prepareCheckout() {
    const items = [...document.querySelectorAll('cart-items, cart-drawer-items')];
    const updates = items.map(element => element.flushQuantityChange?.());
    await Promise.all(updates);
    return enqueue(async () => normalize(await request(cartUrl(''))));
  }

  function showError(form) {
    let error = document.getElementById('cego-cart-quantity-error');
    if (!error) {
      error = document.createElement('p');
      error.id = 'cego-cart-quantity-error';
      error.setAttribute('role', 'alert');
      (form || document.querySelector('main') || document.body).appendChild(error);
    }
    error.textContent = window.cartStrings?.error || 'Unable to verify cart. Please try again.';
  }

  function guardCheckout(event, form, proceed) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    document.getElementById('cego-cart-quantity-error')?.remove();
    prepareCheckout().then(proceed).catch(() => showError(form)).finally(() => { checkoutPending = false; });
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement)) return;
    if (verifiedForms.has(form)) {
      verifiedForms.delete(form);
      return;
    }
    const action = new URL(form.action, window.location.origin);
    if (action.origin !== window.location.origin || !/\/cart\/?$/.test(action.pathname)) return;
    if (submitter?.name !== 'checkout' && !form.querySelector('[name="updates[]"]')) return;
    guardCheckout(event, form, cart => {
      // Keep native submission and app checks, with the server-confirmed quantities.
      [...form.querySelectorAll('[name="updates[]"]')].forEach((input, index) => {
        const item = cart.items[Number(input.dataset.index || index + 1) - 1];
        if (item) input.value = item.quantity;
      });
      verifiedForms.add(form);
      form.requestSubmit(submitter?.isConnected ? submitter : undefined);
    });
  }, true);

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (verifiedLinks.has(link)) {
      verifiedLinks.delete(link);
      return;
    }
    const url = new URL(link.href, window.location.origin);
    if (url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname)) {
      guardCheckout(event, null, () => {
        verifiedLinks.add(link);
        link.click();
      });
    }
  }, true);

  window.CEGO_CART_QUANTITY = { enqueue, request, read: () => request(cartUrl('')), normalize, afterAdd, findItem,
    buildProperties, refreshCartItems, renderPrices, prepareCheckout, sectionsUrl, reportError };
})();
